import clsx from 'clsx'

import { PlayFilledIcon } from '@buffer-mono/popcorn'

import { MediaMarker, MediaTypeMarker } from '~publish/components/MediaMarker'
import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf, ResultOf } from '~publish/graphql'
import { getProxiedThumbnailUrl } from '~publish/legacy/utils/bufferImages'

import styles from './EngagementMediaAsset.module.css'

import type { JSX } from 'react'

export const EngagementMediaAsset_Asset = graphql(/* GraphQL */ `
  fragment EngagementMediaAsset_Asset on Asset @_unmask {
    type
    mimeType
    thumbnail
  }
`)

type EngagementMediaAsset_AssetData = ResultOf<
  typeof EngagementMediaAsset_Asset
>

type EngagementMediaAssetProps = {
  asset: FragmentOf<typeof EngagementMediaAsset_Asset>
  className?: string
  /**
   * Fired when the browser can't fetch the thumbnail. Required, because an
   * asset that keeps rendering after this leaves a broken frame on screen —
   * the owning `EngagementMedia` drops it instead.
   */
  onUnreachable: () => void
}

const getIcon = (asset: EngagementMediaAsset_AssetData): JSX.Element | null => {
  if (asset.type === 'video') {
    return <PlayFilledIcon />
  }

  return null
}

/** A GIF is an image asset served as `image/gif`. */
const isGif = (asset: EngagementMediaAsset_AssetData): boolean =>
  asset.mimeType === 'image/gif'

/**
 * Single attached-media thumbnail for an engagement (comment or mention).
 *
 * GIF assets (`image/gif`) are flagged with a `GIF` badge; the media
 * itself renders as a static thumbnail. (Auto-playing GIFs/videos is
 * tracked separately — see the GIF autoplay ticket.)
 */
export const EngagementMediaAsset = ({
  asset: passedAsset,
  className,
  onUnreachable,
}: EngagementMediaAssetProps): JSX.Element => {
  const asset = readFragment(EngagementMediaAsset_Asset, passedAsset)

  return (
    <div className={clsx(styles.asset, className)}>
      <img
        src={getProxiedThumbnailUrl(asset.thumbnail)}
        alt={asset.type}
        className={styles.thumbnail}
        loading="lazy"
        onError={onUnreachable}
      />
      {getIcon(asset) && (
        <MediaMarker className={styles.badge} aria-hidden>
          {getIcon(asset)}
        </MediaMarker>
      )}
      {isGif(asset) && (
        <MediaTypeMarker type="gif" className={styles.gifBadge} />
      )}
    </div>
  )
}

EngagementMediaAsset.displayName = 'EngagementMediaAsset'
